import { DestroyRef, inject, Injectable } from '@angular/core';
import { TrackError } from '@fantaz/core/utils';
import { Address } from '@fantaz/shared/data-access/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressComponent, GooglePlaceDetail, QueryParams } from './types';

export declare const google: any;
export type Maps = typeof google.maps;

@Injectable({
  providedIn: 'root',
})
export class GoogleScriptInjectService {
  readonly #destroyRef = inject(DestroyRef);
  isInjected = false;
  afterInject$ = new BehaviorSubject<Maps | null>(null);
  _instances = 0;

  private getScriptSrc(callback: string, options: { apiKey: string }): string {
    const query: QueryParams = {
      v: '3',
      callback,
      key: options.apiKey,
      libraries: 'places',
    };
    const params = Object.keys(query)
      .map((key) => `${key}=${query[key]}`)
      .join('&');
    return `//maps.googleapis.com/maps/api/js?${params}&language=en_US`;
  }

  injectScript(options: { apiKey: string }): Observable<Maps | null> {
    if (this.isInjected) {
      return this.afterInject$;
    }
    this.isInjected = true;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    const callbackName = `GooglePlaces_cb_${(Math.random() * 1e9) >>> 0}`;
    script.src = this.getScriptSrc(callbackName, options);

    const promise = new Promise((resolve, reject) => {
      (window as any)[callbackName] = resolve;
      script.onerror = reject;
    });
    document.body.appendChild(script);

    // wait for the script to load and resolve the promise emitted by the callback
    // once the script is loaded, the google.maps object is available
    promise.then((maps: Maps) => {
      this.afterInject$.next(google.maps);
    });

    // emit the google.maps object
    return this.afterInject$;
  }

  get instances(): number {
    return this._instances;
  }

  /**
   * Extracts and formats the first line of an address from Google Maps address
   * components
   *
   * @param {Array<AddressComponent>} addressComponent - Array of Google Maps
   * address components
   * @returns A formatted string containing the street number and route name,
   * with excess whitespace removed
   */
  private getAddressLine1(addressComponent: AddressComponent[]): string {
    const streetNumber = addressComponent.find((component) =>
      component.types.includes('street_number'),
    );

    const route = addressComponent.find((component) =>
      component.types.includes('route'),
    );

    // trim whitespaces and remove multiple spaces
    return formatAddressComponent(
      `${streetNumber?.long_name ?? ''} ${route?.long_name ?? ''}`,
    );
  }

  private getAddressLine2(addressComponent: AddressComponent[]) {
    const addressLine2 = addressComponent.find((component) =>
      component.types.includes('subpremise'),
    );

    return formatAddressComponent(addressLine2?.long_name ?? '');
  }

  private getCity(addressComponent: AddressComponent[]) {
    const city = addressComponent.find((component) =>
      component.types.some((type) =>
        ['locality', 'administrative_area_level_2'].includes(type),
      ),
    );

    return formatAddressComponent(city?.long_name ?? '');
  }

  private getState(addressComponent: AddressComponent[]) {
    const state = addressComponent.find((component) =>
      component.types.includes('administrative_area_level_1'),
    );

    return formatAddressComponent(state?.short_name ?? '');
  }

  private getCountry(addressComponent: AddressComponent[]) {
    const country = addressComponent.find((component) =>
      component.types.includes('country'),
    );

    return formatAddressComponent(country?.short_name ?? '');
  }

  private getZipCode(addressComponent: AddressComponent[]) {
    const zipCode = addressComponent.find((component) =>
      component.types.includes('postal_code'),
    );

    return formatAddressComponent(zipCode?.long_name ?? '');
  }

  transformPlaceToAddress(place: GooglePlaceDetail) {
    const address = new Address({
      addressLine1: this.getAddressLine1(place.address_components),
      secondary: this.getAddressLine2(place.address_components),
      city: this.getCity(place.address_components),
      state: this.getState(place.address_components),
      country: this.getCountry(place.address_components),
      zipCode: this.getZipCode(place.address_components),
    });

    // add error tracking for missing values in the address when user pick a
    // place from the autocomplete list.
    ['addressLine1', 'city', 'state', 'country', 'zipCode'].forEach(
      (key: string) => {
        if (!address[key as keyof Address]) {
          TrackError.trackCustomError(
            'address-google-get-place',
            JSON.stringify(place.address_components),
            `Missing value for ${key}`,
          );
        }
      },
    );

    console.log('address', address);

    return address;
  }
}

function formatAddressComponent(value: string): string {
  return value.trim().replace(/ +(?= )/g, '');
}
